import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  TextField,
  Popover,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { GoodReceivedList, GoodReceivedDelete } from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";

const IssueList = () => {
  const [issues, setIssues] = useState([]);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [issueToDelete, setIssueToDelete] = useState(null);
  const [searchProject, setSearchProject] = useState("");
  const [actionType, setActionType] = useState(null);

  const fetchIssues = async (pageNum = 1, filters = {}) => {
    const res = await GoodReceivedList(pageNum, filters.project_name || "");
    if (res) {
      if (pageNum === 1) {
        setIssues(res.results);
      } else {
        setIssues((prev) => [...prev, ...res.results]);
      }
      setHasMore(res.next !== null);
      setPage(pageNum);
    }
  };


  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchIssues(1, { project_name: searchProject });
    }, 500);
    return () => clearTimeout(handler);
  }, [searchProject]);

  const fetchMoreData = () => {
    fetchIssues(page + 1, { project_name: searchProject });
  };

  const handleAddClick = () => {
    navigate("/dashboard/update");
  };


  const handleEdit = (issue) => {
    // Agar items good_items me aaye hain
    const items = issue.good_items || [];
    // Remove duplicate items by item ID
    const uniqueItems = Array.from(
      new Map(items.map((item) => [item.item, item])).values()
    );

    const cleanedIssue = { ...issue, items: uniqueItems };

    // Ab navigate me correct structure bhej rahe hain
    navigate("/dashboard/update", { state: { issue: cleanedIssue } });
  };


  const handleDelete = async (goodsId) => {
    if (!goodsId) {
      console.error("No Goods Receipt ID provided!");
      toast.error("Cannot delete: Goods Receipt ID is missing.");
      return;
    }

    try {
      await GoodReceivedDelete(goodsId);
      setIssues((prev) =>
        prev.filter((issue) => issue.id !== goodsId)  // ✅ key match karo
      );
      toast.success("Goods Receipt deleted successfully!");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete Goods Receipt. Please try again.");
    }
  };


  return (
    <Box>
      <ToastContainer position="top-right" autoClose={3000} />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap", // allows wrapping on small screens
          alignItems: "center",
          gap: 2, // spacing between items
          width: "100%",
          mt: -3,
          pt: 0,
          mb: 2,
        }}
      >
        {/* ✅ Search field responsive */}
        <TextField
          label="Search by Project Name"
          variant="outlined"
          size="small"
          value={searchProject}
          onChange={(e) => setSearchProject(e.target.value)}
          sx={{
            flex: { xs: "1 1 100%", sm: 1 },
            mb: { xs: 1, sm: 0 },
            "& .MuiInputBase-root": {
              height: 35,           // ✅ compact height
              fontSize: "0.7rem",  // ✅ small text
              borderRadius: "0px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.7rem",  // ✅ label chhota
            },
          }}
        />

        {/* ✅ Button responsive */}
        <Button
          onClick={handleAddClick}
          variant="contained"
          size="small" // ✅ compact button
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          sx={{
            height: 35,           // ✅ same as TextField
            fontSize: "0.7rem",
            padding: "2px 10px",
            borderRadius: "0px",
            minWidth: { xs: "100%", sm: "150px" }, // ✅ smaller desktop width
            whiteSpace: "nowrap",
          }}
        >
           Goods Received
        </Button>

      </Box>

      <InfiniteScroll
        dataLength={issues.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={<h4>Loading...</h4>}
        height={"70vh"}
      >

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 580,
            overflowX: "auto", // horizontal scroll on mobile
            overflowY: "auto",
            width: "100%",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: { xs: "800px", sm: 1600 }, // smaller on mobile, full on desktop
              "& td, & th": {
                textAlign: "center",
                whiteSpace: "nowrap",
                backgroundColor: "#f5f5f5",
                borderBottom: "1px solid #ccc",
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5",
                  "& th": {
                    fontWeight: "bold",
                    color: "#333",
                    borderBottom: "1px solid #999",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#f5f5f5",
                    zIndex: 3,
                  }}
                >
                  S.No
                </TableCell>
                <TableCell>GR No</TableCell>
                <TableCell>Division</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Purchase Order</TableCell>
                <TableCell>Issued By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell
                  align="center"
                  className="no-print"
                  sx={{
                    position: "sticky",
                    right: 0,
                    backgroundColor: "#f5f5f5",
                    zIndex: 3,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {issues.map((issue, index) => (
                <TableRow
                  hover
                  key={index}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                      "& td": { backgroundColor: "#f5f5f5 !important" },
                    },
                    "& td": {
                      paddingTop: '2px !important',
                      paddingBottom: '2px !important',
                      fontSize: { xs: "0.75rem", sm: "0.8125rem" }, // responsive font
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 120,
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "white" : "inherit",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "black" : "inherit",
                    },
                  }}
                >
                  <TableCell sx={{ position: "sticky", left: 0, zIndex: 3 }}>
                    {index + 1}
                  </TableCell>
                  <TableCell> {issue.gr_no}</TableCell>
                  <TableCell>{issue.division_name}</TableCell>
                  <TableCell>{issue.project_name}</TableCell>
                  <TableCell>{issue.purchase_po_number}</TableCell>
                  <TableCell>{issue.created_by_name || issue.created_by}</TableCell>
                  <TableCell>
                    {new Date(issue.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell
                    align="center"
                    className="no-print"
                    sx={{ position: "sticky", right: 0, zIndex: 3 }}
                  >
                    <IconButton color="primary"
                      sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                      onClick={() => handleEdit(issue)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                      color="error"
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        setIssueToDelete(issue.id);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </InfiniteScroll>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setIssueToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={() => {
             
                if (issueToDelete) {
                  handleDelete(issueToDelete);
                  setAnchorEl(null);
                  setIssueToDelete(null);
                }
                else {
                  toast.error("No issue selected for deletion.");
                }
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setIssueToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default IssueList;
